// components/FaviconSwitcher.jsx
import React from 'react';
import { Helmet } from 'react-helmet';
import useManifestIcons from '../../utils/useManifestIcons';

/**
 * FaviconSwitcher updates <link rel="icon"> as well as
 * og:image and twitter:image meta tags based on a flag.
 *
 * @param {boolean} flag – determines which version to show (default vs alternate)
 * @param {(src: string) => string} mapSrcFn – function to map default to alternate image path
 * @param {string} fallbackHref – fallback image path while manifest or icons are unavailable
 */
const FaviconSwitcher = ({
    flag,
    mapSrcFn = (src) => src.replace(/(\.\w+)$/, '-popup$1'),
    fallbackHref = '/favicon.ico',
}: {
    flag: boolean,
    mapSrcFn?: (src: string) => string,
    fallbackHref?: string,
}) => {
    const icons = useManifestIcons();

  // Choose a representative image for Open Graph tags
  const firstIcon = icons.find((icon) => icon.sizes.includes('1200x630')) ||
                    icons.find((icon) => icon.sizes.includes('512x512')) ||
                    icons[0];

  const ogImage = flag && firstIcon ? mapSrcFn(firstIcon.src) :
                   firstIcon?.src || fallbackHref;

  return (
    <Helmet>
      {/* Favicon Links */}
      {icons.length > 0 ? (
        icons.map((icon) => {
          const { src, sizes, type, purpose } = icon;
          const href = flag ? fallbackHref : src;

          return (
            <link
              key={`${href}-${sizes}`}
              rel="icon"
              href={href}
              sizes={sizes}
              type={type || 'image/png'}
              {...(purpose ? { purpose } : {})}
            />
          );
        })
      ) : (
        <link rel="icon" href={fallbackHref} />
      )}

      {/* Social Meta Tags */}
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
};

export default FaviconSwitcher;
